/**
 * Address Normalization Utilities
 *
 * This module provides functions to normalize address data between
 * different formats used in the checkout system.
 *
 * STANDARD FORMAT: Shopify-compatible
 * - address1 (not 'address')
 * - zip (not 'postal_code')
 * - province/province_code (not 'state')
 *
 * This ensures cart recovery works correctly regardless of whether
 * the cart was created via Card flow, PayPal flow, or any other source.
 */

/**
 * Normalize any address format to Shopify standard
 * Handles legacy formats (address, postal_code, state) gracefully
 *
 * @param {Object} addr - Address object in any format
 * @returns {Object|null} Normalized address object or null if input is falsy
 *
 * @example
 * // Legacy format (PayPal)
 * const legacy = { address: '123 Main St', postal_code: '90069', state: 'CA' };
 * const normalized = normalizeAddress(legacy);
 * // Returns: { address1: '123 Main St', zip: '90069', province: 'CA', ... }
 */
export const normalizeAddress = (addr) => {
  if (!addr) return null;

  return {
    first_name: addr.first_name || '',
    last_name: addr.last_name || '',
    // Handle both 'address1' and 'address'
    address1: addr.address1 || addr.address || '',
    address2: addr.address2 || '',
    city: addr.city || '',
    // Handle both 'province' and 'state'
    province: addr.province || addr.state || '',
    province_code: addr.province_code || addr.state_code || '',
    // Handle both 'zip' and 'postal_code'
    zip: addr.zip || addr.postal_code || '',
    country: addr.country || '',
    country_code: addr.country_code || '',
    phone: addr.phone || '',
  };
};

/**
 * Extract address fields from normalized address to form fields
 * Used for pre-filling checkout form from saved cart data
 *
 * @param {Object} addr - Address object (any format, will be normalized)
 * @param {string} prefix - 'shipping' or 'billing'
 * @returns {Object} Form field values keyed by field name
 *
 * @example
 * const cartAddress = { address: '123 Main St', postal_code: '90069' };
 * const fields = addressToFormFields(cartAddress, 'shipping');
 * // Returns: { shippingAddress: '123 Main St', shippingZip: '90069', ... }
 */
export const addressToFormFields = (addr, prefix = 'shipping') => {
  const normalized = normalizeAddress(addr);
  if (!normalized) return {};

  if (prefix === 'shipping') {
    return {
      firstName: normalized.first_name,
      lastName: normalized.last_name,
      shippingAddress: normalized.address1,
      shippingApt: normalized.address2,
      shippingCity: normalized.city,
      shippingState: normalized.province || normalized.province_code,
      shippingZip: normalized.zip,
      country: normalized.country || normalized.country_code,
    };
  }

  // Billing prefix
  return {
    billingFirstName: normalized.first_name,
    billingLastName: normalized.last_name,
    billingAddress: normalized.address1,
    billingApt: normalized.address2,
    billingCity: normalized.city,
    billingState: normalized.province || normalized.province_code,
    billingZip: normalized.zip,
    billingCountry: normalized.country || normalized.country_code,
  };
};

/**
 * Build normalized address from form fields
 * Used when saving cart data or processing checkout
 *
 * @param {Object} formData - Form data object with field values
 * @param {string} prefix - 'shipping' or 'billing'
 * @param {Object} options - Additional options
 * @param {string} options.stateCode - State code (CA, NY, etc.)
 * @param {string} options.countryCode - Country code (US, MX, etc.)
 * @param {string} options.billingStateCode - Billing state code (for billing prefix)
 * @param {string} options.billingCountryCode - Billing country code (for billing prefix)
 * @returns {Object} Normalized address in Shopify format
 *
 * @example
 * const address = formFieldsToAddress(formData, 'shipping', {
 *   stateCode: 'CA',
 *   countryCode: 'US'
 * });
 */
export const formFieldsToAddress = (formData, prefix = 'shipping', options = {}) => {
  const { stateCode, countryCode } = options;

  if (prefix === 'shipping') {
    return {
      first_name: formData.firstName || '',
      last_name: formData.lastName || '',
      address1: formData.shippingAddress || '',
      address2: formData.shippingApt || '',
      city: formData.shippingCity || '',
      province: formData.shippingState || '',
      province_code: stateCode || '',
      zip: formData.shippingZip || '',
      country: formData.country || '',
      country_code: countryCode || '',
    };
  }

  // Billing prefix
  return {
    first_name: formData.billingFirstName || formData.firstName || '',
    last_name: formData.billingLastName || formData.lastName || '',
    address1: formData.billingAddress || '',
    address2: formData.billingApt || '',
    city: formData.billingCity || '',
    province: formData.billingState || '',
    province_code: options.billingStateCode || '',
    zip: formData.billingZip || '',
    country: formData.billingCountry || formData.country || '',
    country_code: options.billingCountryCode || countryCode || '',
  };
};

/**
 * Normalize PayPal shipping address to Shopify format
 * PayPal returns address in a different structure
 *
 * @param {Object} paypalShipping - PayPal shipping address object
 * @returns {Object} Normalized address in Shopify format
 */
export const normalizePayPalAddress = (paypalShipping) => {
  if (!paypalShipping) return null;

  // PayPal may return recipient name as a single string
  const nameParts = (paypalShipping.recipientName || '').split(' ');
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  return normalizeAddress({
    first_name: firstName,
    last_name: lastName,
    address1: paypalShipping.line1 || '',
    address2: paypalShipping.line2 || '',
    city: paypalShipping.city || '',
    province: paypalShipping.state || '',
    zip: paypalShipping.postalCode || '',
    country_code: paypalShipping.countryCode || 'US',
  });
};

export default {
  normalizeAddress,
  addressToFormFields,
  formFieldsToAddress,
  normalizePayPalAddress,
};
